import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AvaliacaoDialog from '../components/AvaliacaoDialog';
import AulasDialog from '../components/AulasDialog';
import OptimizedImage from '../components/OptimizedImage';
import Reviews from '../components/Reviews';
import { mockServices, mockProfessores, contactInfo } from '../data/mock';

const heroSlides = [
  {
    id: 'pt',
    titleLines: ['TREINO', 'PERSONALIZADO'],
    subtitle: 'Foco em si, treino feito à sua medida, resultados reais.',
    cta: 'MARCAR AVALIAÇÃO FÍSICA GRATUITA',
    action: 'avaliacao'
  },
  {
    id: 'pilates',
    titleLines: ['AULAS DE', 'PILATES'],
    subtitle: 'Descobre os benefícios do Pilates no teu dia a dia.',
    cta: 'INSCREVER-ME AGORA',
    action: 'aulas'
  }
];

const Home = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [aulasDialog, setAulasDialog] = useState({ open: false, defaultAula: '' });
  const [currentSlide, setCurrentSlide] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();

  // Auto-loop a cada 5s
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Abrir popup automaticamente via URL: /?popup=aulas ou /?popup=avaliacao
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const popup = params.get('popup');
    if (!popup) return;
    if (popup === 'aulas') {
      setAulasDialog({ open: true, defaultAula: '' });
    } else if (popup === 'avaliacao') {
      setIsFormOpen(true);
    }
    // Limpar query string para evitar reabrir ao fechar/navegar (sem causar reload)
    const next = location.pathname + (location.hash || '');
    navigate(next, { replace: true });
  }, [location.search, location.pathname, location.hash, navigate]);

  const handleSlideCta = (action) => {
    if (action === 'aulas') {
      setAulasDialog({ open: true, defaultAula: 'pilates' });
    } else {
      setIsFormOpen(true);
    }
  };

  return (
    <div className="min-h-screen">
      <Header onOpenForm={() => setIsFormOpen(true)} />

      {/* Hero - Vídeo + Slider de conteúdo (desktop + mobile unificado) */}
      <section className="relative bg-black min-h-screen overflow-hidden" data-testid="home-hero">
        {/* Background: Vídeo */}
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          ref={(el) => {
            if (el) {
              el.play().catch(err => console.log('Video autoplay prevented:', err));
            }
          }}
          className="absolute top-0 left-0 w-full h-full"
          style={{ objectFit: 'cover', zIndex: 0 }}
        >
          <source src="/assets/03_hero_web.webm" type="video/webm" />
          <source src="/assets/03_hero_mp4.mp4" type="video/mp4" />
        </video>

        {/* Overlay escuro */}
        <div className="absolute inset-0 bg-black/55" style={{ zIndex: 1 }} />

        {/* Container do slider - posicionado consistentemente em desktop e mobile */}
        <div
          className="hero-slider-container absolute z-[2]"
          style={{
            left: 'clamp(20px, 8vw, 11%)',
            right: 'clamp(20px, 4vw, 6%)',
            top: '50%',
            transform: 'translateY(-50%)',
            maxWidth: '900px',
            minHeight: 'clamp(300px, 40vh, 480px)'
          }}
        >
          {heroSlides.map((slide, idx) => (
            <div
              key={slide.id}
              data-testid={`hero-slide-${slide.id}`}
              aria-hidden={currentSlide !== idx}
              className="absolute inset-0 transition-opacity duration-700 ease-in-out flex flex-col items-start justify-center"
              style={{
                opacity: currentSlide === idx ? 1 : 0,
                pointerEvents: currentSlide === idx ? 'auto' : 'none'
              }}
            >
              {/* Título com blocos pretos por linha */}
              <div className="mb-4 sm:mb-5 flex flex-col items-start">
                {slide.titleLines.map((line, i) => (
                  <span
                    key={i}
                    className="hero-title-line uppercase text-white"
                    style={{
                      backgroundColor: '#000000',
                      fontFamily: "'BebasNeue-Book', sans-serif",
                      fontWeight: 'normal',
                      fontSize: 'clamp(40px, 6.7vw, 101px)',
                      letterSpacing: '0.05em',
                      lineHeight: '1',
                      display: 'inline-flex',
                      alignItems: 'center',
                      paddingTop: '7px',
                      paddingBottom: '7px',
                      paddingLeft: 'clamp(12px, 1.5vw, 22px)',
                      paddingRight: 'clamp(12px, 1.5vw, 22px)',
                      marginBottom: i === 0 ? '6px' : '0'
                    }}
                  >
                    {line}
                  </span>
                ))}
              </div>

              {/* Subtítulo */}
              <p
                className="hero-subtitle m-0 mb-7 sm:mb-9 text-white max-w-[640px]"
                style={{
                  fontFamily: "'BebasNeue-Book', sans-serif",
                  fontWeight: 'normal',
                  fontSize: 'clamp(16px, 2.2vw, 32px)',
                  letterSpacing: '0.02em',
                  lineHeight: '1.3',
                  textShadow: '2px 2px 4px rgba(0,0,0,0.7)'
                }}
              >
                {slide.subtitle}
              </p>

              {/* CTA */}
              <Button
                onClick={() => handleSlideCta(slide.action)}
                data-testid={`hero-slide-${slide.id}-cta`}
                className="hero-cta btn-mobile-cta bg-yellow-400 hover:bg-yellow-500 text-black uppercase px-10 py-6 border-2 border-yellow-400 hover:shadow-2xl transform hover:-translate-y-1 transition-all"
                style={{
                  fontFamily: "'BebasNeue-Book', sans-serif",
                  fontWeight: 'normal',
                  fontSize: 'clamp(17px, 1.85vw, 28px)',
                  letterSpacing: '-0.02em'
                }}
              >
                {slide.cta}
              </Button>
            </div>
          ))}
        </div>

        {/* Indicador de Localização — desktop (lado direito da hero) */}
        <div
          className="hidden md:flex absolute z-[3] items-center gap-3 select-none pointer-events-none"
          style={{
            right: 'clamp(60px, 10vw, 220px)',
            top: '50%',
            transform: 'translateY(-50%)'
          }}
          data-testid="hero-location-desktop"
          aria-hidden="true"
        >
          <img
            src="/assets/icon-location.png"
            alt=""
            width="48"
            height="48"
            loading="lazy"
            className="w-[40px] md:w-[44px] lg:w-[48px] h-auto"
            style={{ filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.55))' }}
          />
          <span
            className="text-white uppercase"
            style={{
              fontFamily: "'BebasNeue-Book', sans-serif",
              fontWeight: 'normal',
              fontSize: 'clamp(28px, 3.4vw, 50px)',
              letterSpacing: '0.04em',
              lineHeight: '1',
              textShadow: '0 2px 6px rgba(0,0,0,0.55)'
            }}
          >
            OVAR
          </span>
        </div>

        {/* Indicador de Localização — mobile (canto inferior direito da hero) */}
        <div
          className="md:hidden absolute z-[3] flex items-center gap-2 select-none pointer-events-none"
          style={{
            right: '20px',
            bottom: 'clamp(28px, 6vh, 64px)'
          }}
          data-testid="hero-location-mobile"
          aria-hidden="true"
        >
          <img
            src="/assets/icon-location.png"
            alt=""
            width="32"
            height="32"
            loading="lazy"
            className="w-[28px] h-auto"
            style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.55))' }}
          />
          <span
            className="text-white uppercase"
            style={{
              fontFamily: "'BebasNeue-Book', sans-serif",
              fontWeight: 'normal',
              fontSize: '20px',
              letterSpacing: '0.06em',
              lineHeight: '1',
              textShadow: '0 2px 4px rgba(0,0,0,0.55)'
            }}
          >
            OVAR
          </span>
        </div>

        {/* Indicadores de slide (quadrados) — posicionados fixos na hero */}
        <div
          className="absolute z-[3] flex items-center gap-2"
          style={{
            left: 'clamp(20px, 8vw, 11%)',
            bottom: 'clamp(28px, 6vh, 64px)'
          }}
          data-testid="hero-indicators"
        >
          {heroSlides.map((_, idx) => (
            <button
              key={idx}
              type="button"
              aria-label={`Ir para slide ${idx + 1}`}
              aria-current={currentSlide === idx}
              data-testid={`hero-indicator-${idx}`}
              onClick={() => setCurrentSlide(idx)}
              className="transition-all duration-300 hover:scale-110"
              style={{
                width: '14px',
                height: '14px',
                border: '1.5px solid #ffffff',
                backgroundColor: currentSlide === idx ? '#ffffff' : 'transparent',
                borderRadius: 0,
                padding: 0,
                cursor: 'pointer'
              }}
            />
          ))}
        </div>
      </section>

      {/* Nova Secção: Ultimate Fit - Compromisso */}
      <section className="py-16 md:py-24 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 lg:gap-16 items-center">
            
            {/* Imagem da Equipa (Esquerda) */}
            <div className="order-1">
              <picture>
                <source 
                  media="(min-width: 1024px)" 
                  srcSet="/assets/team-ultimate-desktop.webp" 
                  type="image/webp"
                />
                <source 
                  media="(min-width: 768px)" 
                  srcSet="/assets/team-ultimate-tablet.webp" 
                  type="image/webp"
                />
                <source 
                  media="(max-width: 767px)" 
                  srcSet="/assets/team-ultimate-mobile.webp" 
                  type="image/webp"
                />
                <img
                  src="/assets/team-ultimate-desktop.webp"
                  alt="Equipa Ultimate Fit"
                  loading="lazy"
                  className="w-full h-auto shadow-2xl"
                />
              </picture>
            </div>

            {/* Conteúdo de Texto (Direita) */}
            <div className="order-2">
              {/* Label */}
              <p 
                className="text-yellow-400 uppercase tracking-wider text-sm md:text-base mb-4 font-semibold"
                style={{ 
                  fontFamily: "'BebasNeue-Book', sans-serif",
                  letterSpacing: "0.15em"
                }}
              >
                ULTIMATE FIT
              </p>

              {/* Título Principal */}
              <h2 
                className="text-3xl md:text-4xl lg:text-5xl text-white mb-6 leading-tight"
                style={{ 
                  fontFamily: "'BebasNeue-Book', sans-serif",
                  fontWeight: "normal",
                  letterSpacing: "0.02em",
                  lineHeight: "1.2"
                }}
              >
                Um objetivo claro. Um compromisso sólido: a sua evolução.
              </h2>

              {/* Texto Descritivo */}
              <p 
                className="text-gray-300 text-base md:text-lg leading-relaxed mb-8"
                style={{ 
                  fontFamily: "'Montserrat', sans-serif",
                  fontWeight: 300,
                  fontSize: "0.9em",
                  letterSpacing: "0.01em",
                  lineHeight: "1.7"
                }}
              >
                A nossa metodologia centra-se em si, cuidando de cada pormenor para potenciar o seu progresso. Combinamos treino personalizado, um plano alimentar adaptado às suas metas e um acompanhamento contínuo, assegurando resultados consistentes e duradouros. Mais do que mudanças físicas, pretendemos promover um novo estilo de vida com mais energia, autoconfiança e equilíbrio.
              </p>

              {/* Botão CTA - IDÊNTICO AO HERO */}
              <Button
                onClick={() => setIsFormOpen(true)}
                className="btn-mobile-cta bg-yellow-400 hover:bg-yellow-500 text-black uppercase px-10 py-6 border-2 border-yellow-400 hover:shadow-2xl transform hover:-translate-y-1 transition-all"
                style={{ 
                  fontFamily: "'BebasNeue-Book', sans-serif", 
                  fontWeight: "normal",
                  fontSize: "33.6px",
                  letterSpacing: "-0.02em"
                }}
              >
                <span style={{ fontFamily: "'BebasNeue-Book', sans-serif", fontWeight: "normal" }}>MARCAR AVALIAÇÃO FÍSICA GRATUITA</span>
              </Button>
            </div>

          </div>
        </div>
      </section>

      {/* Serviços Section */}
      <section id="servicos" className="py-16 sm:py-20 md:py-24 bg-black" data-testid="servicos-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-5xl md:text-6xl text-white mb-4 px-4 section-title" style={{ fontFamily: "'BebasNeue-Book', sans-serif", fontWeight: "normal", fontSize: "130px", letterSpacing: "0.05em" }}>SERVIÇOS</h2>
            <p className="text-lg sm:text-xl text-gray-300 px-4" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>Comece hoje a construir a sua melhor versão</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {mockServices.map((service) => (
              <Link
                key={service.id}
                to={service.link}
                data-testid={`servico-card-${service.id}`}
                className="group flex flex-col"
              >
                {/* Imagem vertical com título em overlay */}
                <div className="relative overflow-hidden bg-zinc-900 h-[360px] md:h-[420px]">
                  <img
                    src={service.image}
                    alt={service.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                    style={{ objectPosition: 'center 30%' }}
                  />
                  {/* Gradient overlay para legibilidade */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent transition-opacity duration-500 group-hover:from-black/90" />
                  {/* Título sobre a imagem */}
                  <h3
                    className="absolute left-0 right-0 bottom-5 px-5 text-white uppercase text-center"
                    style={{
                      fontFamily: "'BebasNeue-Book', sans-serif",
                      fontWeight: "normal",
                      fontSize: "clamp(22px, 1.85vw, 30px)",
                      letterSpacing: "0.05em",
                      lineHeight: "1.05",
                      textShadow: "0 2px 8px rgba(0,0,0,0.55)"
                    }}
                  >
                    {service.title}
                  </h3>
                </div>

                {/* Botão SABER MAIS por baixo da imagem */}
                <div className="pt-5 flex justify-center">
                  <span
                    className="btn-cut-corner"
                    style={{
                      fontFamily: "'BebasNeue-Book', sans-serif",
                      fontSize: "16px",
                      letterSpacing: "0.08em"
                    }}
                  >
                    SABER MAIS
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Equipa Section */}
      <section id="equipa" className="py-16 sm:py-20 md:py-24 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-5xl md:text-6xl text-black mb-4 px-4 section-title" style={{ fontFamily: "'BebasNeue-Book', sans-serif", fontWeight: "normal", fontSize: "130px", letterSpacing: "0.05em" }}>EQUIPA</h2>
            <p className="text-lg sm:text-xl text-gray-600 px-4" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>Um único propósito, um único compromisso</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 lg:gap-12 max-w-5xl mx-auto mb-12">
            {mockProfessores.slice(0, 3).map((trainer) => (
              <Link key={trainer.id} to={`/${trainer.slug || trainer.id}`} className="group cursor-pointer">
                <div className="relative overflow-hidden bg-black aspect-[3/4]">
                  <OptimizedImage
                    src={trainer.foto}
                    alt={trainer.nome}
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-110 transition-all duration-[400ms] ease-out"
                    style={{ objectPosition: 'center 18%' }}
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-20 transition-opacity duration-[400ms]"></div>
                </div>
                <div className="mt-6 text-center">
                  <h3 className="text-2xl md:text-3xl text-black group-hover:text-yellow-400 transition-colors duration-300 mb-2" style={{ fontFamily: "'BebasNeue-Book', sans-serif", fontWeight: "normal" }}>
                    {trainer.nome}
                  </h3>
                  <p className="text-base md:text-lg text-gray-600" style={{ fontFamily: "'BebasNeue-Book', sans-serif", fontWeight: "normal", letterSpacing: "-0.01em" }}>
                    {trainer.role}
                  </p>
                </div>
              </Link>
            ))}
          </div>
          <div className="text-center px-4">
            <Link to="/equipa">
              <Button className="btn-mobile-cta cut-corner-shape w-full sm:w-auto bg-black hover:bg-gray-800 text-white px-6 sm:px-8 py-5 sm:py-6 text-[20px] border-2 border-black transition-colors" style={{ fontFamily: "'BebasNeue-Book', sans-serif", fontWeight: "normal", letterSpacing: "-0.02em" }}>
                VER TODOS <ArrowRight className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Follow / #BESTRONG Strip */}
      <section className="bg-black" data-testid="follow-bestrong-section">
        {/* Header da secção */}
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-10">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <a
              href={contactInfo.socials.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 group"
              data-testid="follow-bestrong-link"
            >
              <span
                className="text-white uppercase"
                style={{
                  fontFamily: "'BebasNeue-Book', sans-serif",
                  fontSize: "clamp(14px, 1vw, 16px)",
                  letterSpacing: "0.25em"
                }}
              >
                Follow
              </span>
              <span
                className="uppercase transition-transform duration-300 group-hover:translate-x-1"
                style={{
                  fontFamily: "'BebasNeue-Book', sans-serif",
                  fontSize: "clamp(14px, 1vw, 16px)",
                  letterSpacing: "0.25em",
                  color: "#facc15"
                }}
              >
                #BESTRONG
              </span>
            </a>

            <a
              href={contactInfo.socials.instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="bestrong-cta-btn"
              className="btn-cut-corner"
              style={{
                fontFamily: "'BebasNeue-Book', sans-serif",
                fontSize: "16px",
                letterSpacing: "0.08em"
              }}
            >
              SEGUE-NOS NO INSTAGRAM
              <ArrowRight size={16} strokeWidth={2.5} />
            </a>
          </div>
        </div>

        {/* Faixa horizontal de imagens (galeria visual — temporária, não é feed real) */}
        <a
          href={contactInfo.socials.instagram}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Instagram Ultimate Fit"
          className="block group"
        >
          <div
            className="flex overflow-x-auto md:overflow-hidden no-scrollbar"
            style={{ scrollSnapType: 'x mandatory' }}
          >
            {[
              { src: '/assets/bestrong-1.webp', alt: 'Ultimate Fit - Estúdio' },
              { src: '/assets/bestrong-2.webp', alt: 'Ultimate Fit - Treino' },
              { src: '/assets/bestrong-3.webp', alt: 'Ultimate Fit - Equipa' },
              { src: '/assets/bestrong-4.webp', alt: 'Ultimate Fit - Aulas' },
              { src: '/assets/bestrong-5.webp', alt: 'Ultimate Fit - Personal Training' },
              { src: '/assets/bestrong-6.webp', alt: 'Ultimate Fit - #BESTRONG' }
            ].map((img, i) => (
              <div
                key={i}
                className="relative flex-shrink-0 w-[260px] md:w-auto md:flex-1 overflow-hidden bg-zinc-900"
                style={{ scrollSnapAlign: 'start', height: 'clamp(220px, 22vw, 260px)' }}
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]"
                  style={{ objectPosition: 'center 30%' }}
                />
                <div className="absolute inset-0 bg-black/15 group-hover:bg-black/0 transition-colors duration-500" />
              </div>
            ))}
          </div>
        </a>
      </section>

      {/* Secção Google Reviews */}
      <Reviews />

      <Footer />
      <AvaliacaoDialog isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} />
      <AulasDialog
        isOpen={aulasDialog.open}
        onClose={() => setAulasDialog({ open: false, defaultAula: '' })}
        defaultAula={aulasDialog.defaultAula}
      />
    </div>
  );
};

export default Home;

